/**
 * AI Summaries feature documentation page
 */

import {DocsContent} from '../../../components/docs/DocsContent';
import {CodeBlock} from '../../../components/docs/CodeBlock';
import {DocsActionButtons} from '../../../components/docs/DocsActionButtons';
import {Link} from 'react-router-dom';

export function Summaries() {
  return (
    <DocsContent
      title="AI Summaries"
      description="Automated meeting summaries, action items, and key insights powered by AI."
    >
      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Overview</h2>
          <p className="text-textSecondary mb-4">
            VoxaPilot automatically generates intelligent summaries of your meetings, extracting key
            information, action items, and insights. This saves you hours of manual note-taking.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Summary Types</h2>

          <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">Meeting Summary</h3>
          <p className="text-textSecondary mb-4">
            A high-level overview of the meeting, including main topics discussed, key points, and
            overall context.
          </p>

          <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">Action Items</h3>
          <p className="text-textSecondary mb-4">
            Automatically extracted tasks and follow-ups, assigned to specific speakers when
            possible.
          </p>

          <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">Key Decisions</h3>
          <p className="text-textSecondary mb-4">
            Important decisions made during the meeting, with context and rationale.
          </p>

          <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">Insights</h3>
          <p className="text-textSecondary mb-4">
            Analytics and insights including sentiment analysis, participation metrics, and topic
            distribution.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Generating Summaries</h2>
          <p className="text-textSecondary mb-4">
            Summaries are automatically generated after a meeting ends. You can also manually
            trigger summary generation or regenerate with different settings.
          </p>

          <CodeBlock
            code={`// Generate a summary for a meeting
const summary = await api.meetings.generateSummary(meetingId, {
  includeActionItems: true,
  includeDecisions: true,
  includeInsights: true,
  model: 'gpt-4-turbo', // Optional: specify model
});

console.log('Summary:', summary.summary);
console.log('Action Items:', summary.actionItems);
console.log('Decisions:', summary.decisions);
console.log('Insights:', summary.insights);`}
            language="javascript"
          />
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Customization</h2>
          <p className="text-textSecondary mb-4">
            Customize summary generation to match your needs:
          </p>

          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>Choose summary length (brief, standard, detailed)</li>
            <li>Select which elements to include</li>
            <li>Set custom prompts for specific use cases</li>
            <li>Configure action item extraction rules</li>
            <li>Set up automatic summary sharing</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Best Practices</h2>
          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>Review and edit summaries for accuracy</li>
            <li>Assign action items to team members</li>
            <li>Share summaries with meeting participants</li>
            <li>Use summaries as meeting notes</li>
            <li>Search through summaries to find past discussions</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Next Steps</h2>
          <ul className="list-disc list-inside space-y-2 text-textSecondary dark:text-[#858585]">
            <li>
              <Link to="/docs/features/analytics" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Explore analytics features
              </Link>
            </li>
            <li>
              <Link to="/docs/models" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Learn about AI models
              </Link>
            </li>
            <li>
              <Link to="/docs/api/endpoints" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Use the summaries API
              </Link>
            </li>
          </ul>
        </section>

        <DocsActionButtons pageTitle="AI Summaries" />
      </div>
    </DocsContent>
  );
}

